import type { IconType } from '@evisort/eds/types/SharedIconProps';

import { IconButton } from '@evisort/eds/components/Button/subcomponents/IconButton';
import { Icon } from '@evisort/eds/components/Icon/Icon';
import { Text } from '@evisort/eds/components/Text/Text';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router';

import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import type { NotificationType, PlatformNotification } from './types';

import './NotificationTray.css';

const messages = defineMessages({
  markAsRead: {
    id: 'NotificationTray.markAsRead',
    defaultMessage: 'Mark as read',
    description: 'Button that marks a single notification as read',
  },
  dismiss: {
    id: 'NotificationTray.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Button that dismisses a single notification',
  },
});

const DEFAULT_NOTIFICATION_TYPE: NotificationType = 'info';

const NOTIFICATION_TYPE_ICON: Record<NotificationType, IconType> = {
  ai: 'ai-sparkle',
  approval: 'checklist-approval',
  failure: 'block',
  form: 'form',
  info: 'info',
  success: 'check-circle',
};

type NotificationRowProps = {
  notification: PlatformNotification;
  onClick: (notification: PlatformNotification) => void;
};

export function NotificationRow({
  notification,
  onClick,
}: NotificationRowProps) {
  const intl = useIntl();
  const formatDate = useFormatDate();

  const {
    createdAt,
    description,
    href,
    isDismissable,
    isRead,
    onDismiss,
    onMarkAsRead,
    title,
    type = DEFAULT_NOTIFICATION_TYPE,
  } = notification;

  const icon = NOTIFICATION_TYPE_ICON[type];

  const rowContent = (
    <>
      <span
        aria-hidden="true"
        className="notification-tray__dot-slot"
        data-testid={isRead ? undefined : 'notification-unread-indicator'}
        data-unread={!isRead}
      />
      <span className="notification-tray__icon" data-type={type}>
        <Icon icon={icon} size="s" />
      </span>
      <span className="notification-tray__body">
        <Text as="p" variant="body-m">
          {title}
        </Text>
        {description && (
          <Text as="p" tone="subdued" variant="body-s">
            {description}
          </Text>
        )}
      </span>
      <span className="notification-tray__timestamp">
        <Text tone="subdued" variant="body-s">
          {formatDate(createdAt, 'month_day')}
        </Text>
      </span>
    </>
  );

  return (
    <li className="notification-tray__row">
      {href ? (
        <Link
          className="notification-tray__row-main"
          to={href}
          onClick={() => onClick(notification)}
        >
          {rowContent}
        </Link>
      ) : (
        <div className="notification-tray__row-main">{rowContent}</div>
      )}
      <div className="notification-tray__actions">
        {onMarkAsRead && !isRead && (
          <IconButton
            icon={{ key: 'check' }}
            label={intl.formatMessage(messages.markAsRead)}
            size="s"
            tooltip={{ text: intl.formatMessage(messages.markAsRead) }}
            variant="tertiary"
            onClick={onMarkAsRead}
          />
        )}
        {isDismissable && onDismiss && (
          <IconButton
            icon={{ key: 'x' }}
            label={intl.formatMessage(messages.dismiss)}
            size="s"
            tooltip={{ text: intl.formatMessage(messages.dismiss) }}
            variant="tertiary"
            onClick={onDismiss}
          />
        )}
      </div>
    </li>
  );
}
